import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { tenantApi } from '../../../api/endpoints/tenant';
import { ErrorState } from '@fiado/react-kit';
import { handleApiError, resolveErrorMessage } from '../../../api/errorMapping';
import { ContentLoading } from '../../../components/shared/ContentLoading';
import { Button } from '../../../components/ui/button';
import { EditTenantSecuritySection } from '../tenants/EditTenantSecuritySection';
import { securityCannotSave, type SecurityDraft } from '../../../lib/securityPolicy';

// La política de seguridad de la PLATAFORMA misma (no la de un tenant del catálogo). La ficha platform
// es un tenant más, así que su política vive en el mismo endpoint `/tenants/{tid}/security-policy` con
// tid='platform' (espejo exacto del Branding de platform → `/tenants/platform/branding`).
const PLATFORM_TID = 'platform';
const QUERY_KEY = ['platform', 'security-policy', PLATFORM_TID] as const;

/**
 * Editor de la política de seguridad del módulo Platform. Wrapper delgado: hace el `useQuery` y, una vez
 * cargada la política, monta el form interno con el draft inicial. Espejo conceptual de
 * `PlatformBrandingEditor` (que envuelve `BrandingEditorForm`); reusa la sección presentacional
 * `EditTenantSecuritySection` (2FA on/off + métodos EMAIL/TOTP), igual que el modal de tenant.
 *
 * Diferencia con el branding: el branding usa react-hook-form + conversión HSL↔hex; aquí el "form" es un
 * par de booleanos (toggle + checkboxes), así que el draft es un `useState<SecurityDraft>` plano sin RHF.
 * El draft vive en el componente interno `SecurityForm`, montado SOLO cuando ya hay datos → el estado se
 * inicializa una sola vez sin necesidad de un `useEffect` (evita el cascading-render del linter).
 */
export function PlatformSecurityEditor() {
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: () => tenantApi.getSecurityPolicy(PLATFORM_TID),
    });

    if (isError) {
        return (
            <ErrorState
                title="No pudimos cargar la política de seguridad"
                message={resolveErrorMessage(error)}
                retryLabel="Reintentar"
                onRetry={() => {
                    void refetch();
                }}
            />
        );
    }
    if (isLoading || !data) return <ContentLoading />;

    // `mfaEnabled` default `true` secure-by-default si el GET no lo trae (mismo criterio que el modal de
    // tenant). `methodsAllowed` siempre presente en el shape (Required) → no necesita default.
    // key derivado de los datos: tras Guardar+invalidar, el refetch trae los valores nuevos → el key cambia
    // → React REMONTA SecurityForm y su useState(initial) se reinicializa con ellos, reflejando el estado
    // real sin necesidad de hard reload (antes el form conservaba el draft viejo).
    return (
        <SecurityForm
            key={`${String(data.mfaEnabled)}-${data.methodsAllowed.join(',')}`}
            initial={{ mfaEnabled: data.mfaEnabled ?? true, methodsAllowed: data.methodsAllowed }}
        />
    );
}

function SecurityForm({ initial }: { initial: SecurityDraft }) {
    const qc = useQueryClient();
    const [draft, setDraft] = useState<SecurityDraft>(initial);

    const mut = useMutation({
        mutationFn: (next: SecurityDraft) => tenantApi.updateSecurityPolicy(PLATFORM_TID, next),
        onSuccess: async () => {
            toast.success('Política de seguridad actualizada');
            await qc.invalidateQueries({ queryKey: QUERY_KEY });
        },
        onError: handleApiError,
    });

    // Dirty-gating (igual que el branding): el botón Guardar aparece SOLO cuando el draft difiere del
    // estado cargado. Comparación de methodsAllowed orden-insensible. Tras guardar+invalidar, el form se
    // remonta con el nuevo `initial` → vuelve a no-dirty y el botón desaparece.
    const isDirty =
        draft.mfaEnabled !== initial.mfaEnabled ||
        draft.methodsAllowed.length !== initial.methodsAllowed.length ||
        draft.methodsAllowed.some((m) => !initial.methodsAllowed.includes(m));

    return (
        <div className="space-y-4">
            {/* Platform es siempre email-primary → el invariante usa 'EMAIL' como principal. */}
            <EditTenantSecuritySection
                value={draft}
                onChange={setDraft}
                disabled={mut.isPending}
                principalIdentifier="EMAIL"
            />

            {isDirty && (
                <Button
                    onClick={() => {
                        mut.mutate(draft);
                    }}
                    disabled={mut.isPending || securityCannotSave(draft, 'EMAIL')}
                >
                    {mut.isPending ? 'Guardando…' : 'Guardar'}
                </Button>
            )}
        </div>
    );
}
